import { selectActiveElementId } from "@/store/selections-selectors";
import { useAppSelector } from "@/store/store-hooks";
import {
  FaroIconButton,
  FaroText,
  SchoolIcon,
  useDialog,
} from "@faro-lotv/flat-ui";
import { useApiClientContext } from "@faro-lotv/service-wires";
import { JsonEditor } from "json-edit-react";
import { useCallback, type JSX } from "react";

/**
 * @returns button that fetches and displays VLM (Visual Language Model) analysis data
 * for the currently active element in a dialog with a JSON viewer.
 */
export function VlmAnalysisTool(): JSX.Element {
  const { createDialog } = useDialog();
  const { projectApiClient: projectApi } = useApiClientContext();

  const activeElementId = useAppSelector(selectActiveElementId);

  const openDialog = useCallback(async () => {
    if (!activeElementId) return;

    let analysis;
    try {
      analysis = await projectApi.vlmAnalysis(activeElementId);
    } catch {
      // Ignore error, will just show simple text instead
    }

    createDialog({
      title: "VLM analysis data",
      size: "l",
      content: analysis ? (
        <JsonEditor
          data={analysis}
          collapse={0}
          showStringQuotes={false}
          indent={1}
          restrictEdit
          restrictDelete
          restrictAdd
        />
      ) : (
        <FaroText variant="bodyL">No analysis</FaroText>
      ),

      confirmText: "Close",
      showCancelButton: false,
      allowToCloseWithoutCancelling: false,
    });
  }, [activeElementId, projectApi, createDialog]);

  return (
    <FaroIconButton onClick={openDialog} size="xs">
      <SchoolIcon />
    </FaroIconButton>
  );
}
